<!-- Course Card Container - Conditionally renders horizontal or vertical layout -->
<div class="w-full rounded-[20px] overflow-hidden">
    <!-- ---------------------------- -->
    <!-- Horizontal Layout -->
    <div *ngIf="isHorizontalLayout; else verticalLayout" class="horizontal-layout">
        <!-- Course Image Section - Horizontal Layout -->
        <div class="program-image-container">
            <a [routerLink]="[courseListing.programRoute, courseListing.id]">
                <img
                    class="program-image"
                    alt="course cover image"
                    [src]="courseListing.attachmentUrl || GENERAL_IMAGE_PLACEHOLDER" />
            </a>
        </div>
        
        <!-- Course Details Section - Horizontal Layout -->
        <div class="program-details-container">
            <!-- Tags and Favorite Button Container - Horizontal Layout -->
            <div class="tag-container flex items-center justify-between">
                <!-- Tags Section - Horizontal Layout -->
                <div class="flex items-center gap-2 min-w-0 flex-1">
                    <!-- Category/Topic Tag - Horizontal Layout -->
                    @if (topicUi) {
                        <button
                            #tooltip="matTooltip"
                            type="button"
                            class="tag cursor-pointer text-left !text-base !py-1 !px-[10px] !flex-shrink-1"
                            [matTooltip]="categories"
                            (click)="tooltip.toggle()">
                            {{ categories || '-' }}
                        </button>
                    } @else {
                        <!-- Language Tag - Horizontal Layout -->
                        <div class="pill-black rounded-lg text-left ellipsis-line-1 !w-fit !py-1 !px-[10px]">
                            {{ getLongLanguageOptions(courseListing.language).label | translate }}
                        </div>
                    }

                    <!-- Credit Hours Tag - Horizontal Layout -->
                    <div class="pill-yellow flex items-center flex-shrink-0 gap-x-2 !py-1 !px-[10px]">
                        <mat-icon svgIcon="clock" class="!h-[1.15rem] relative"></mat-icon>
                        <div class="ellipsis-line-1">
                            <span>
                                {{ (courseListing.creditHours | number: '1.1-1') || 0 }}
                                {{ (courseListing.creditHours > 1 ? 'text.hoursFull' : 'text.hourFull') | translate }}
                            </span>
                            @if (courseListing.uiProgramType === UiProgramType.LiveWebinar) {
                                <span class="inline-block w-0.5 border-r-[#6D4904] border-r-2"></span>
                                {{ 'text.cpe' | translate }}
                            }
                        </div>
                    </div>
                </div>
                
                <!-- Favorite Button - Horizontal Layout -->
                <app-favourite
                    [programId]="courseListing.id"
                    [isFavourite]="courseListing.isFavorite"
                    (favourite)="updateBookmarkUi()">
                </app-favourite>
            </div>

            <!-- Course Title - Horizontal Layout -->
            <div class="ellipsis-line-1 mt-4">
                <a
                    class="text-[#231F20] font-semibold text-lg hover:text-primary"
                    [routerLink]="[courseListing.programRoute, courseListing.id]"
                    >{{ courseListing.title }}</a
                >
            </div>

            <!-- Course Description - Horizontal Layout -->
            <div class="ellipsis-line-2 mt-3">
                <p class="text-[#5F5B5B] font-normal text-base">{{ courseListing.about }}</p>
            </div>

            <!-- Action Button - Horizontal Layout -->
            <div class="mt-12">
                <app-program-button
                    class="card-action-button"
                    [certificateId]="courseListing.certificateAttachmentId"
                    [completedAt]="courseListing.completedAt"
                    [isEnrol]="courseListing.isEnrol"
                    [isProgramExpired]="courseListing.isProgramExpired"
                    [isProgramJoinable]="courseListing.isProgramJoinable"
                    [isLearningInProgress]="courseListing.progress > 0"
                    [isLearningCompleted]="courseListing.progress === 100"
                    [programRoute]="courseListing.programRoute"
                    [programId]="courseListing.id"
                    [uiProgramType]="courseListing.uiProgramType"
                    [willIncludeRecording]="courseListing.willIncludeRecording"
                    [isMeetingEnded]="courseListing.isMeetingEnded"
                    [details]="courseListing"></app-program-button>
            </div>
        </div>
    </div>
    <!-- ---------------------------- -->
    <!-- Vertical Layout -->
    <ng-template #verticalLayout>
        <!-- Course Image Section - Vertical Layout -->
        <div class="program-image-container">
            <a [routerLink]="[courseListing.programRoute, courseListing.id]">
                <img
                    class="program-image"
                    alt="course cover image"
                    [src]="courseListing.attachmentUrl || GENERAL_IMAGE_PLACEHOLDER" />
            </a>
        </div>
        
        <!-- Course Details Section - Vertical Layout -->
        <div class="program-details-container w-full h-full p-5 bg-[#f3f4f5] relative">
            <!-- Favorite Button - Vertical Layout (Top Right Position) -->
            <div class="absolute -top-4 right-3">
                <app-favourite
                    [programId]="courseListing.id"
                    [isFavourite]="courseListing.isFavorite"
                    (favourite)="updateBookmarkUi()"></app-favourite>
            </div>
            
            <!-- Tags Container - Vertical Layout -->
            <div class="tag-container">
                <!-- Tags Section - Vertical Layout -->
                <div class="flex items-center gap-2 min-w-0 flex-1">
                    <!-- Category/Topic Tag - Vertical Layout -->
                    @if (topicUi) {
                        <button
                            #tooltip="matTooltip"
                            type="button"
                            class="tag cursor-pointer text-left !text-base !py-1 !px-[10px] min-w-0 flex-shrink"
                            [matTooltip]="categories"
                            (click)="tooltip.toggle()">
                            {{ categories || '-' }}
                        </button>
                    } @else {
                        <!-- Language Tag - Vertical Layout -->
                        <div class="pill-black rounded-lg text-left ellipsis-line-1 !w-fit !py-1 !px-[10px] min-w-0 flex-shrink">
                            {{ getLongLanguageOptions(courseListing.language).label | translate }}
                        </div>
                    }

                    <!-- Credit Hours Tag - Vertical Layout -->
                    <div class="pill-yellow flex items-center flex-shrink-0 gap-x-2 !py-1 !px-[10px]">
                        <mat-icon svgIcon="clock" class="!h-[1.15rem] relative"></mat-icon>
                        <div class="ellipsis-line-1">
                            <span>
                                {{ (courseListing.creditHours | number: '1.1-1') || 0 }}
                                {{ (courseListing.creditHours > 1 ? 'text.hoursFull' : 'text.hourFull') | translate }}
                            </span>
                            @if (courseListing.uiProgramType === UiProgramType.LiveWebinar) {
                                <span class="inline-block w-0.5 border-r-[#6D4904] border-r-2"></span>
                                {{ 'text.cpe' | translate }}
                            }
                        </div>
                    </div>
                </div>
            </div>

            <!-- Course Title - Vertical Layout -->
            <div class="ellipsis-line-1 mt-4">
                <a
                    class="text-[#231F20] font-semibold text-lg hover:text-primary"
                    [routerLink]="[courseListing.programRoute, courseListing.id]"
                    >{{ courseListing.title }}</a
                >
            </div>

            <!-- Course Description - Vertical Layout -->
            <div class="mt-3 ellipsis-line-2 h-12">
                <p class="text-[#5F5B5B] font-normal text-base">{{ courseListing.about }}</p>
            </div>

            <!-- Action Button - Vertical Layout -->
            <div class="mt-5">
                <app-program-button
                    class="card-action-button"
                    [certificateId]="courseListing.certificateAttachmentId"
                    [completedAt]="courseListing.completedAt"
                    [isEnrol]="courseListing.isEnrol"
                    [isProgramExpired]="courseListing.isProgramExpired"
                    [isProgramJoinable]="courseListing.isProgramJoinable"
                    [isLearningInProgress]="courseListing.progress > 0"
                    [isLearningCompleted]="courseListing.progress === 100"
                    [programRoute]="courseListing.programRoute"
                    [programId]="courseListing.id"
                    [uiProgramType]="courseListing.uiProgramType"
                    [willIncludeRecording]="courseListing.willIncludeRecording"
                    [isMeetingEnded]="courseListing.isMeetingEnded"
                    [details]="courseListing"></app-program-button>
            </div>
        </div>
    </ng-template>
    <!-- ---------------------------- -->
</div>
